import { memo, ReactNode } from 'react';
import { Stack, styled, SxProps, Tooltip, Typography } from '@mui/material';
import { PaperTabView } from 'modules/common/components/TabsWithSections/PaperTabView';
import { PaperTabViewProps } from 'modules/common/components/TabsWithSections/PaperTabView/PaperTabView';
import { FlexBox } from '@dfl/mui-react-common';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import { InventoryHelpDashboard } from 'modules/reports/help/constants/help-anchors';
import ChartHelpButton from 'modules/reports/help/components/ChartHelpButton';

export type FormPaperProps = PaperTabViewProps & {
  title?: string | ReactNode;
  subtitle?: any;
  helpText?: string;
  helpId?: string;
  helpDashboard?: InventoryHelpDashboard;
  className?: string;
  actions?: any;
  mbHeader?: string | number;
  headerSx?: SxProps;
  titleSx?: SxProps;
  divider?: boolean;
  variant?: {
    title: 'h1' | 'subtitle2' | 'h4';
  };
  icon?: ReactNode;
};

const FormPaper = ({
  title,
  children,
  divider,
  actions,
  mbHeader,
  headerSx,
  helpText,
  helpId,
  helpDashboard,
  variant,
  icon,
  subtitle,
  titleSx,
  ...props
}: FormPaperProps) => {
  const hasHeader = !!title || !!actions;
  const hasHelpButton = !!helpId && !!helpDashboard;
  return (
    <PaperTabView {...props} sx={{ position: 'relative', borderRadius: 2, ...((props as any).sx ?? {}) }}>
      {hasHelpButton && (
        <ChartHelpButton helpDashboard={helpDashboard} helpId={helpId} />
      )}
      {hasHeader && (
        <Stack sx={{ ...titleSx, gap: 0.4, marginBottom: mbHeader || 2 }}>
          <FlexBox flexWrap={'wrap'} gap={2} alignItems='center' justifyContent={actions ? 'space-between' : 'start'}>
            <Stack gap={0.5} alignItems={'center'} direction={'row'}>
              {!!icon && <IconContent>{icon}</IconContent>}
              {!!title && (
                <Typography variant={variant?.title ?? 'subtitle1'} sx={headerSx}>
                  {title}
                </Typography>
              )}
              {!!helpText && (
                <Tooltip title={helpText} placement='right' arrow>
                  <HelpOutlineOutlinedIcon fontSize='small' />
                </Tooltip>
              )}
            </Stack>
            {actions}
          </FlexBox>
          {!!subtitle && (
            typeof subtitle === 'string' ? (
              <Typography fontWeight={300} variant='subtitle2'>
                {subtitle}
              </Typography>
            ) : (
              subtitle
            )
          )}
        </Stack>
      )}

      {children}
    </PaperTabView>
  );
};

export default memo(FormPaper);

const IconContent = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  padding: 4,
  borderRadius: 4,
}));
